import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { Button, Input, Skeleton, Typography, notification } from "antd";
import {
    ArrowLeftOutlined,
    CopyOutlined,
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import AppShell, { AutoSyncBadge } from "Components/Layout/AppShell";
import AllocationBar from "Components/Dashboard/AllocationBar";
import AddWalletDrawer from "Components/Forms/AddWalletDrawer";
import { Tokens } from "Shared/Tokens";
import { getFormattedAmount, formatSmartPrice } from "Shared/Utils";
import getCurrencySymbol from "Shared/CurrancySymbol";
import {
    deleteWallet,
    setSelectedWallet,
    setWalletBalanceCache,
    updateWallet,
} from "store/Actions/wallets";
import { fetchOnChainWallet, formatShortAddress } from "utils/Wallets";

const { Title, Text } = Typography;

const WalletsView = () => {
    const history = useHistory();
    const { uid: routeUid } = useParams();
    const dispatch = useDispatch();

    const wallets = useSelector((state) => state.wallets?.wallets || []);
    const selectedWallet = useSelector(
        (state) => state.wallets?.selectedWallet || {}
    );
    const walletBalances = useSelector(
        (state) => state.wallets?.walletBalances || {}
    );
    const displayCurrency = useSelector(
        (state) => state.settings?.currency || "USDT"
    );

    const [activeUid, setActiveUid] = useState(
        routeUid || selectedWallet?.uid || wallets[0]?.uid || ""
    );
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [showAddDrawer, setShowAddDrawer] = useState(false);
    const [editingName, setEditingName] = useState(false);
    const [draftName, setDraftName] = useState("");
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    useEffect(() => {
        if (routeUid && wallets.some((w) => w.uid === routeUid)) {
            setActiveUid(routeUid);
        } else if (!activeUid && wallets.length > 0) {
            setActiveUid(wallets[0].uid);
        } else if (
            activeUid &&
            wallets.length > 0 &&
            !wallets.some((w) => w.uid === activeUid)
        ) {
            setActiveUid(wallets[0].uid);
        }
    }, [routeUid, wallets, activeUid]);

    const currentWallet =
        wallets.find((w) => w.uid === activeUid) || wallets[0] || null;

    const getCacheKey = (w) =>
        w
            ? `${String(w.chain || "solana").toLowerCase()}:${String(
                  w.address || ""
              )
                  .trim()
                  .toLowerCase()}`
            : "";

    const currentBalanceData = currentWallet
        ? walletBalances[getCacheKey(currentWallet)] || null
        : null;

    const loadWalletBalance = useCallback(
        async (walletObj, { force = false } = {}) => {
            if (!walletObj || !walletObj.address) return;
            if (force) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }
            try {
                const data = await fetchOnChainWallet(
                    walletObj.chain,
                    walletObj.address,
                    {
                        currency: displayCurrency,
                        refresh: force,
                    }
                );
                if (data) {
                    dispatch(
                        setWalletBalanceCache(
                            walletObj.chain,
                            walletObj.address,
                            data
                        )
                    );
                }
            } catch (err) {
                notification.error({
                    message: "Could not refresh wallet",
                    description:
                        "The network RPC did not respond in time. Showing last cached snapshot.",
                    key: "wallet-fetch-error",
                });
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [dispatch, displayCurrency]
    );

    useEffect(() => {
        if (currentWallet && !currentBalanceData) {
            loadWalletBalance(currentWallet, { force: false });
        }
    }, [currentWallet, currentBalanceData, loadWalletBalance]);

    const handleSelectWalletTab = (walletObj) => {
        setActiveUid(walletObj.uid);
        setEditingName(false);
        setConfirmingDelete(false);
        dispatch(setSelectedWallet(walletObj));
        history.replace(`/wallets/${walletObj.uid}`);
    };

    const handleCopyAddress = (addr) => {
        if (
            typeof navigator !== "undefined" &&
            navigator.clipboard &&
            typeof navigator.clipboard.writeText === "function"
        ) {
            navigator.clipboard.writeText(addr).catch(() => {});
        }
        notification.success({
            message: "Address copied",
            description: formatShortAddress(addr),
            key: "wallet-address-copied",
        });
    };

    const handleSaveRename = () => {
        if (!currentWallet) return;
        const clean = String(draftName || "").trim();
        if (clean) {
            dispatch(updateWallet({ uid: currentWallet.uid, name: clean }));
        }
        setEditingName(false);
    };

    const handleConfirmDelete = () => {
        if (!currentWallet) return;
        const deletedUid = currentWallet.uid;
        dispatch(deleteWallet(deletedUid));
        setConfirmingDelete(false);
        const remaining = wallets.filter((w) => w.uid !== deletedUid);
        if (remaining.length > 0) {
            setActiveUid(remaining[0].uid);
            history.replace(`/wallets/${remaining[0].uid}`);
        } else {
            history.push("/portfolios");
        }
    };

    const currSymbol = getCurrencySymbol(
        currentBalanceData?.currency || displayCurrency || "USDT"
    );

    const chain =
        String(currentWallet?.chain || "solana").toLowerCase() === "ethereum"
            ? "ethereum"
            : "solana";
    const isEth = chain === "ethereum";
    const nativeSymbol = isEth ? "ETH" : "SOL";
    const nativeName = isEth ? "Ethereum" : "Solana";

    const nativeQty = Number(currentBalanceData?.balance) || 0;
    const nativePrice = Number(currentBalanceData?.nativePrice) || 0;
    const nativeValue = Number(currentBalanceData?.nativeValueUsd) || 0;
    const tokens = Array.isArray(currentBalanceData?.tokens)
        ? currentBalanceData.tokens
        : [];
    const totalValue = Number(currentBalanceData?.totalValueUsd) || 0;

    // Combine native token + SPL/ERC20 tokens into one unified ranked asset list
    const combinedHoldings = [
        {
            key: `native-${nativeSymbol}`,
            symbol: nativeSymbol,
            name: nativeName,
            isNative: true,
            contractAddress: "Native coin",
            quantity: nativeQty,
            price: nativePrice,
            value: nativeValue,
        },
        ...tokens.map((t) => ({
            key: t.mint || t.contractAddress || t.symbol,
            symbol: t.symbol || "TOKEN",
            name: t.name || t.symbol || "Token",
            isNative: false,
            contractAddress: t.contractAddress || t.mint || "",
            quantity: Number(t.quantity ?? t.tokens) || 0,
            price: Number(t.currentPrice) || 0,
            value: Number(t.valueUsd ?? t.balance?.usd) || 0,
        })),
    ].sort((a, b) => b.value - a.value);

    const allocationItems = combinedHoldings
        .filter((h) => h.value > 0)
        .map((h) => ({
            symbol: h.symbol,
            name: h.name,
            value: h.value,
        }));

    return (
        <AppShell
            activeTab="overview"
            title={
                <div
                    style={{
                        maxWidth: Tokens.containerMaxWidth,
                        margin: "0 auto",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                        gap: "12px",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            minWidth: 0,
                        }}
                    >
                        <Button
                            data-testid="wallets-back-btn"
                            icon={<ArrowLeftOutlined />}
                            onClick={() => history.push("/portfolios")}
                            style={{
                                width: "36px",
                                height: "36px",
                                padding: 0,
                                borderRadius: Tokens.radiusControl,
                                backgroundColor: Tokens.raised,
                                borderColor: Tokens.hairline,
                                color: Tokens.textPrimary,
                                flexShrink: 0,
                            }}
                        />
                        <Title
                            level={4}
                            style={{
                                margin: 0,
                                color: Tokens.textPrimary,
                                fontWeight: 500,
                                fontSize: "18px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            On-chain wallets
                        </Title>
                        <AutoSyncBadge />
                    </div>

                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                        }}
                    >
                        {currentWallet && (
                            <Button
                                data-testid="refresh-wallet-btn"
                                icon={<ReloadOutlined spin={refreshing} />}
                                onClick={() =>
                                    loadWalletBalance(currentWallet, {
                                        force: true,
                                    })
                                }
                                style={{
                                    width: "36px",
                                    height: "36px",
                                    padding: 0,
                                    borderRadius: Tokens.radiusControl,
                                    backgroundColor: Tokens.raised,
                                    borderColor: Tokens.hairline,
                                    color: Tokens.textSecondary,
                                }}
                            />
                        )}
                        <Button
                            data-testid="header-add-wallet-btn"
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setShowAddDrawer(true)}
                            style={{
                                height: "36px",
                                borderRadius: Tokens.radiusControl,
                                fontWeight: 500,
                                padding: "0 12px",
                            }}
                        >
                            Add wallet
                        </Button>
                    </div>
                </div>
            }
        >
            <div
                data-testid="wallets-view-container"
                style={{
                    maxWidth: Tokens.containerMaxWidth,
                    margin: "0 auto",
                    width: "100%",
                }}
            >
                {wallets.length === 0 ? (
                    <div
                        data-testid="wallets-empty-state"
                        style={{
                            padding: "32px 20px",
                            borderRadius: Tokens.radiusCard,
                            border: `1px solid ${Tokens.hairline}`,
                            backgroundColor: Tokens.card,
                            textAlign: "center",
                        }}
                    >
                        <Title
                            level={4}
                            style={{
                                margin: "0 0 8px 0",
                                color: Tokens.textPrimary,
                                fontWeight: 500,
                                fontSize: "18px",
                            }}
                        >
                            No wallets connected
                        </Title>
                        <Text
                            style={{
                                display: "block",
                                color: Tokens.textSecondary,
                                fontSize: "14px",
                                maxWidth: "400px",
                                margin: "0 auto 20px auto",
                            }}
                        >
                            Track Solana SPL and Ethereum ERC-20 holdings by
                            adding a public wallet address
                        </Text>
                        <Button
                            data-testid="empty-add-wallet-btn"
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setShowAddDrawer(true)}
                            style={{
                                height: "40px",
                                borderRadius: Tokens.radiusControl,
                                fontWeight: 500,
                                padding: "0 20px",
                            }}
                        >
                            Add Solana or Ethereum wallet
                        </Button>
                    </div>
                ) : (
                    <React.Fragment>
                        {wallets.length > 1 && (
                            <div
                                data-testid="wallets-switcher-bar"
                                style={{
                                    display: "flex",
                                    gap: "8px",
                                    flexWrap: "wrap",
                                    marginBottom: "16px",
                                }}
                            >
                                {wallets.map((w) => {
                                    const active =
                                        currentWallet &&
                                        w.uid === currentWallet.uid;
                                    const badge =
                                        w.chain === "ethereum" ? "ETH" : "SOL";
                                    return (
                                        <button
                                            key={w.uid}
                                            type="button"
                                            data-testid={`switch-wallet-tab-${w.uid}`}
                                            onClick={() =>
                                                handleSelectWalletTab(w)
                                            }
                                            style={{
                                                height: "36px",
                                                padding: "0 12px",
                                                borderRadius:
                                                    Tokens.radiusControl,
                                                border: `1px solid ${
                                                    active
                                                        ? Tokens.accentBorder
                                                        : Tokens.hairline
                                                }`,
                                                backgroundColor: active
                                                    ? Tokens.accentSubtle
                                                    : Tokens.card,
                                                color: active
                                                    ? Tokens.textPrimary
                                                    : Tokens.textSecondary,
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                cursor: "pointer",
                                                fontSize: "13px",
                                                fontWeight: active ? 500 : 400,
                                            }}
                                        >
                                            <span>{w.name}</span>
                                            <span
                                                style={{
                                                    fontFamily: Tokens.fontMono,
                                                    fontSize: "11px",
                                                    color: active
                                                        ? Tokens.accent
                                                        : Tokens.textMuted,
                                                }}
                                            >
                                                {badge}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {currentWallet && (
                            <div
                                data-testid="active-wallet-hero-card"
                                style={{
                                    padding: "20px",
                                    borderRadius: Tokens.radiusCard,
                                    border: `1px solid ${Tokens.hairline}`,
                                    backgroundColor: Tokens.card,
                                    marginBottom: "16px",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        gap: "12px",
                                        marginBottom: "16px",
                                    }}
                                >
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontFamily: Tokens.fontMono,
                                                    fontSize: "11px",
                                                    padding: "2px 6px",
                                                    borderRadius:
                                                        Tokens.radiusTag,
                                                    backgroundColor:
                                                        Tokens.raised,
                                                    border: `1px solid ${Tokens.hairlineStrong}`,
                                                    color: Tokens.textSecondary,
                                                }}
                                            >
                                                {nativeSymbol}
                                            </span>

                                            {editingName ? (
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "8px",
                                                    }}
                                                >
                                                    <Input
                                                        data-testid="rename-wallet-input"
                                                        value={draftName}
                                                        onChange={(e) =>
                                                            setDraftName(
                                                                e.target.value
                                                            )
                                                        }
                                                        style={{
                                                            height: "32px",
                                                            width: "180px",
                                                            backgroundColor:
                                                                Tokens.raised,
                                                            borderColor:
                                                                Tokens.hairline,
                                                            color: Tokens.textPrimary,
                                                            borderRadius:
                                                                Tokens.radiusControl,
                                                        }}
                                                    />
                                                    <Button
                                                        data-testid="save-rename-wallet-btn"
                                                        size="small"
                                                        type="primary"
                                                        onClick={
                                                            handleSaveRename
                                                        }
                                                    >
                                                        Save
                                                    </Button>
                                                    <Button
                                                        data-testid="cancel-rename-wallet-btn"
                                                        size="small"
                                                        onClick={() =>
                                                            setEditingName(
                                                                false
                                                            )
                                                        }
                                                    >
                                                        Cancel
                                                    </Button>
                                                </div>
                                            ) : (
                                                <React.Fragment>
                                                    <span
                                                        data-testid="active-wallet-title"
                                                        style={{
                                                            color: Tokens.textPrimary,
                                                            fontSize: "16px",
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {currentWallet.name}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        data-testid="edit-wallet-name-btn"
                                                        onClick={() => {
                                                            setDraftName(
                                                                currentWallet.name
                                                            );
                                                            setEditingName(
                                                                true
                                                            );
                                                        }}
                                                        style={{
                                                            background: "none",
                                                            border: "none",
                                                            padding: "4px",
                                                            color: Tokens.textMuted,
                                                            cursor: "pointer",
                                                        }}
                                                    >
                                                        <EditOutlined />
                                                    </button>
                                                </React.Fragment>
                                            )}
                                        </div>

                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                marginTop: "8px",
                                            }}
                                        >
                                            <span
                                                data-testid="active-wallet-address"
                                                style={{
                                                    fontFamily: Tokens.fontMono,
                                                    fontSize: "12px",
                                                    color: Tokens.textMuted,
                                                }}
                                            >
                                                {formatShortAddress(
                                                    currentWallet.address
                                                )}
                                            </span>
                                            <button
                                                type="button"
                                                data-testid="copy-wallet-address-btn"
                                                onClick={() =>
                                                    handleCopyAddress(
                                                        currentWallet.address
                                                    )
                                                }
                                                style={{
                                                    background: "none",
                                                    border: "none",
                                                    padding: "2px 4px",
                                                    color: Tokens.textSecondary,
                                                    cursor: "pointer",
                                                    fontSize: "12px",
                                                }}
                                            >
                                                <CopyOutlined />
                                            </button>
                                        </div>
                                    </div>

                                    {!confirmingDelete ? (
                                        <button
                                            type="button"
                                            data-testid="remove-wallet-trigger-btn"
                                            onClick={() =>
                                                setConfirmingDelete(true)
                                            }
                                            style={{
                                                background: "none",
                                                border: `1px solid ${Tokens.hairline}`,
                                                borderRadius:
                                                    Tokens.radiusControl,
                                                height: "32px",
                                                padding: "0 10px",
                                                color: Tokens.textMuted,
                                                cursor: "pointer",
                                                fontSize: "12px",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: "6px",
                                            }}
                                        >
                                            <DeleteOutlined />
                                            <span>Remove</span>
                                        </button>
                                    ) : (
                                        <div
                                            data-testid="confirm-delete-wallet-bar"
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                            }}
                                        >
                                            <Button
                                                data-testid="confirm-delete-wallet-btn"
                                                size="small"
                                                danger
                                                onClick={handleConfirmDelete}
                                            >
                                                Confirm
                                            </Button>
                                            <Button
                                                data-testid="cancel-delete-wallet-btn"
                                                size="small"
                                                onClick={() =>
                                                    setConfirmingDelete(false)
                                                }
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    )}
                                </div>

                                <div
                                    style={{
                                        color: Tokens.textMuted,
                                        fontSize: "12px",
                                        marginBottom: "4px",
                                    }}
                                >
                                    Wallet balance
                                </div>
                                <div
                                    data-testid="active-wallet-total-value"
                                    style={{
                                        color: Tokens.textPrimary,
                                        fontSize: "28px",
                                        fontWeight: 500,
                                        fontFamily: Tokens.fontMono,
                                        fontVariantNumeric: "tabular-nums",
                                        lineHeight: 1.15,
                                    }}
                                >
                                    {currSymbol}
                                    {getFormattedAmount(totalValue, true, 2)}
                                </div>

                                <div
                                    style={{
                                        marginTop: "16px",
                                        paddingTop: "12px",
                                        borderTop: `1px solid ${Tokens.hairline}`,
                                        display: "grid",
                                        gridTemplateColumns:
                                            "repeat(3, minmax(0, 1fr))",
                                        gap: "12px",
                                        fontVariantNumeric: "tabular-nums",
                                    }}
                                >
                                    <div>
                                        <div
                                            style={{
                                                color: Tokens.textMuted,
                                                fontSize: "12px",
                                                marginBottom: "4px",
                                            }}
                                        >
                                            Native {nativeSymbol}
                                        </div>
                                        <div
                                            data-testid="active-wallet-native-balance"
                                            style={{
                                                color: Tokens.textPrimary,
                                                fontSize: "14px",
                                                fontWeight: 500,
                                                fontFamily: Tokens.fontMono,
                                            }}
                                        >
                                            {formatSmartPrice(nativeQty)}{" "}
                                            {nativeSymbol}
                                        </div>
                                    </div>

                                    <div>
                                        <div
                                            style={{
                                                color: Tokens.textMuted,
                                                fontSize: "12px",
                                                marginBottom: "4px",
                                            }}
                                        >
                                            {isEth
                                                ? "ERC-20 value"
                                                : "SPL value"}
                                        </div>
                                        <div
                                            style={{
                                                color: Tokens.textSecondary,
                                                fontSize: "14px",
                                                fontFamily: Tokens.fontMono,
                                            }}
                                        >
                                            {currSymbol}
                                            {getFormattedAmount(
                                                Number(
                                                    currentBalanceData?.tokensValueUsd
                                                ) || 0,
                                                true,
                                                2
                                            )}
                                        </div>
                                    </div>

                                    <div style={{ textAlign: "right" }}>
                                        <div
                                            style={{
                                                color: Tokens.textMuted,
                                                fontSize: "12px",
                                                marginBottom: "4px",
                                            }}
                                        >
                                            Tokens
                                        </div>
                                        <div
                                            style={{
                                                color: Tokens.textPrimary,
                                                fontSize: "14px",
                                                fontWeight: 500,
                                                fontFamily: Tokens.fontMono,
                                            }}
                                        >
                                            {combinedHoldings.length}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {allocationItems.length > 0 && (
                            <AllocationBar
                                items={allocationItems}
                                totalValue={totalValue}
                                currencySymbol={currSymbol}
                            />
                        )}

                        <div
                            data-testid="wallet-holdings-list"
                            style={{
                                borderRadius: Tokens.radiusCard,
                                border: `1px solid ${Tokens.hairline}`,
                                backgroundColor: Tokens.card,
                                overflow: "hidden",
                            }}
                        >
                            <div
                                style={{
                                    padding: "12px 16px",
                                    borderBottom: `1px solid ${Tokens.hairline}`,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <span
                                    style={{
                                        color: Tokens.textSecondary,
                                        fontSize: "13px",
                                        fontWeight: 500,
                                    }}
                                >
                                    Holdings ({combinedHoldings.length})
                                </span>
                                <span
                                    style={{
                                        color: Tokens.textMuted,
                                        fontSize: "12px",
                                    }}
                                >
                                    Value ({displayCurrency})
                                </span>
                            </div>

                            {loading && !currentBalanceData ? (
                                <div style={{ padding: "20px" }}>
                                    <Skeleton active paragraph={{ rows: 3 }} />
                                </div>
                            ) : (
                                combinedHoldings.map((item, idx) => {
                                    const sharePct =
                                        totalValue > 0
                                            ? (item.value / totalValue) * 100
                                            : 0;
                                    return (
                                        <div
                                            key={item.key}
                                            data-testid={`wallet-holding-row-${item.symbol}`}
                                            style={{
                                                padding: "16px",
                                                borderBottom:
                                                    idx <
                                                    combinedHoldings.length - 1
                                                        ? `1px solid ${Tokens.hairline}`
                                                        : "none",
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                gap: "12px",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "12px",
                                                    minWidth: 0,
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: "36px",
                                                        height: "36px",
                                                        borderRadius:
                                                            Tokens.radiusControl,
                                                        backgroundColor:
                                                            Tokens.raised,
                                                        border: `1px solid ${Tokens.hairline}`,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent:
                                                            "center",
                                                        color: Tokens.textPrimary,
                                                        fontFamily:
                                                            Tokens.fontMono,
                                                        fontSize: "11px",
                                                        fontWeight: 500,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {String(
                                                        item.symbol || ""
                                                    ).slice(0, 4)}
                                                </div>
                                                <div style={{ minWidth: 0 }}>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            gap: "8px",
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                color: Tokens.textPrimary,
                                                                fontSize:
                                                                    "14px",
                                                                fontWeight: 500,
                                                            }}
                                                        >
                                                            {item.symbol}
                                                        </span>
                                                        <span
                                                            style={{
                                                                color: Tokens.textMuted,
                                                                fontSize:
                                                                    "12px",
                                                            }}
                                                        >
                                                            {item.name}
                                                        </span>
                                                    </div>
                                                    <div
                                                        style={{
                                                            color: Tokens.textSecondary,
                                                            fontSize: "12px",
                                                            fontFamily:
                                                                Tokens.fontMono,
                                                            fontVariantNumeric:
                                                                "tabular-nums",
                                                            marginTop: "4px",
                                                        }}
                                                    >
                                                        {formatSmartPrice(
                                                            item.quantity
                                                        )}{" "}
                                                        {item.symbol}
                                                        {item.price > 0
                                                            ? ` · ${currSymbol}${formatSmartPrice(
                                                                  item.price
                                                              )}`
                                                            : ""}
                                                    </div>
                                                </div>
                                            </div>

                                            <div
                                                style={{
                                                    textAlign: "right",
                                                    fontVariantNumeric:
                                                        "tabular-nums",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        color: Tokens.textPrimary,
                                                        fontSize: "14px",
                                                        fontWeight: 500,
                                                        fontFamily:
                                                            Tokens.fontMono,
                                                    }}
                                                >
                                                    {currSymbol}
                                                    {getFormattedAmount(
                                                        item.value,
                                                        true,
                                                        2
                                                    )}
                                                </div>
                                                <div
                                                    style={{
                                                        color: Tokens.textMuted,
                                                        fontSize: "12px",
                                                        fontFamily:
                                                            Tokens.fontMono,
                                                        marginTop: "4px",
                                                    }}
                                                >
                                                    {sharePct.toFixed(1)}%
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </React.Fragment>
                )}
            </div>

            <AddWalletDrawer
                open={showAddDrawer}
                onClose={() => setShowAddDrawer(false)}
                currency={displayCurrency}
                onWalletAdded={(newWallet) => {
                    if (newWallet?.uid) {
                        setActiveUid(newWallet.uid);
                    }
                }}
            />
        </AppShell>
    );
};

export default WalletsView;
